/**
 * `Card` — ported from `Foyer_Dashboard_CURRENT.jsx` line 234.
 * Thin-border, small-radius surface container.
 */

import type { CSSProperties, KeyboardEventHandler, MouseEventHandler, ReactNode } from 'react';

import { useC } from '../../lib/theme.js';

export interface CardProps {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  onClick?: MouseEventHandler<HTMLDivElement>;
  /** Pass-through accessibility attributes — used when a Card serves as
   *  an interactive surface (e.g. the VisibilityWidget). The base atom
   *  stays unopinionated; callers wire the role/tabIndex/keyboard
   *  handlers themselves. */
  role?: string;
  tabIndex?: number;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  'aria-label'?: string;
  /** Forwarded so callers can anchor tests to a specific Card without
   *  a wrapping div. Prior to 2026-07-08 this was silently dropped —
   *  a small trap for testids passed by call sites (e.g. `<Card
   *  data-testid="onboarding-woo-connect">`). Made explicit here so
   *  the intent is one prop, not a whole rest-spread. */
  'data-testid'?: string;
  /** Forwarded id — used by Settings deep links (`settings_section`) to
   *  scroll a specific Card into view. */
  id?: string;
}

export function Card({
  children,
  className = '',
  style,
  onClick,
  role,
  tabIndex,
  onKeyDown,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
  id,
}: CardProps): JSX.Element {
  const C = useC();
  return (
    <div
      id={id}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role={role}
      tabIndex={tabIndex}
      aria-label={ariaLabel}
      data-testid={dataTestId}
      className={`relative ${className}`}
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 4,
        ...style,
      }}
    >
      {children}
    </div>
  );
}
